/* FENN custom: makes the main-menu category links ("Camisetas", "Calças", "Moletons",
 * "Mais Vendidos") jump/scroll to that category's product section on the homepage
 * instead of opening a separate collection page.
 *
 * Why this can't just be a plain "#id" anchor: the section headings are rendered from
 * a Shopify rich-text section setting (templates/index.json), and Shopify's server-side
 * sanitizer for that field strips an `id` attribute if we add one to the heading HTML
 * (confirmed: themeFilesUpsert rejects it outright). So instead, this script finds the
 * right section at runtime by matching its heading's visible text, and scrolls to it
 * itself (with an offset so the sticky header doesn't cover it), rather than depending
 * on the browser's native same-page hash jump.
 *
 * The menu items themselves (Shopify Admin > Online Store > Navigation) were changed to
 * point to "/#camisetas", "/#calcas", "/#moletons", "/#mais-vendidos" — plain links that
 * still work (they land on the homepage) even if this script fails to load for any reason.
 *
 * REWRITE NOTE: earlier versions of this file drove the scroll animation by hand with our
 * own requestAnimationFrame loop, tracking which scroll container to use, computing the
 * target position, easing toward it frame by frame. That turned out to be the wrong
 * foundation — every bug found while chasing real reports (stale position from a still-
 * loading hero video, rAF getting paused when the tab is backgrounded, a delayed click's
 * callback firing after a newer one) was a direct consequence of reimplementing something
 * the browser already does natively, better. This version instead calls the browser's own
 * `Element.scrollIntoView({ behavior: 'smooth' })`, which: finds the correct scrolling
 * ancestor itself (no more guessing which element scrolls at which width — see the removed
 * getScroller()); runs as a native, compositor-driven animation, not paused by the same
 * backgrounding that throttles page JavaScript; and, if called again with a new target
 * before the previous scroll finishes, simply redirects toward the new target on its own
 * (no need for us to track and cancel a previous animation by hand).
 *
 * FIX (found from a screen recording — thank you for that, it's what made this findable):
 * every click used to call `history.pushState(...)`, which adds a BRAND NEW entry to the
 * browser's back/forward history — same as if a new page had actually loaded. Click three
 * categories in a row and there are now three new "pages" stacked behind the real one. Any
 * accidental back-navigation gesture while scrolling (a mouse's tilt-wheel/side button, or
 * a trackpad swipe — very easy to trigger by accident while spinning a scroll wheel) walks
 * back through that stack, and because this store's analytics/tracking scripts keep the
 * page from being eligible for the browser's instant back/forward cache, landing on one of
 * those older entries forces Chrome to do a full network reload — which is exactly what it
 * looks like happened in the recording: the page reloads back to the very top, the URL
 * loses its "#camisetas"/"#calcas", and the site is unresponsive for a moment while it
 * reloads (matching "parece que saí da janela do navegador"). Using `history.replaceState`
 * instead updates the address bar the same way but does NOT create a new history entry, so
 * there is nothing left for an accidental back-navigation to stumble into.
 */
(function () {
  var ANCHORS = {
    'mais-vendidos': 'Mais Vendidos',
    camisetas: 'Camisetas',
    regatas: 'Regatas',
    moletons: 'Moletons',
    calcas: 'Calças',
  };

  function findSectionHeading(label) {
    var headings = document.querySelectorAll(
      '.shopify-section h1, .shopify-section h2, .shopify-section h3, .shopify-section h4, .shopify-section h5, .shopify-section h6'
    );
    for (var i = 0; i < headings.length; i++) {
      if (headings[i].textContent.trim() === label) return headings[i];
    }
    return null;
  }

  // Still needed even with native scrollIntoView: the URL-hash update below happens on a
  // delay (so the drawer's own closing animation finishes first, see the click handler),
  // and a visitor clicking a second category before that delay elapses would otherwise let
  // the OLDER click's delayed callback overwrite the newer click's URL after the fact. Each
  // navigation attempt claims a ticket number; a stale one checks it's still the latest
  // before touching the URL, and quietly does nothing if a newer click has since taken over.
  var latestNavToken = 0;

  function scrollToHeading(el) {
    var headerHeight = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 96;
    // scroll-margin-top tells scrollIntoView how much extra space to leave above the
    // element — exactly what's needed so the sticky header doesn't end up covering it.
    el.style.scrollMarginTop = headerHeight + 24 + 'px';
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function scrollToAnchor(key) {
    var label = ANCHORS[key];
    if (!label) return false;

    var el = findSectionHeading(label);
    if (!el) return false;

    scrollToHeading(el);
    return true;
  }

  // Handles arriving at the homepage with one of these hashes already in the URL
  // (e.g. clicked the menu from a product page, or a bookmarked/shared link).
  function handleInitialHash() {
    var key = window.location.hash.replace('#', '');
    if (!ANCHORS[key]) return;

    // Small delay so images/fonts have settled enough for the target's position to be
    // accurate — otherwise the page can still shift after we've already scrolled.
    window.setTimeout(function () {
      scrollToAnchor(key);
    }, 100);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', handleInitialHash);
  } else {
    handleInitialHash();
  }

  // Intercepts clicks on any link pointing at one of these hashes (main nav, mobile
  // drawer menu, footer, etc.) so that clicking while already on the homepage animates
  // straight to the section instead of relying on the browser's native (instant, and
  // header-covered) same-page hash jump.
  document.addEventListener('click', function (event) {
    var link = event.target.closest('a[href*="#"]');
    if (!link) return;

    var url;
    try {
      url = new URL(link.href, window.location.href);
    } catch (e) {
      return;
    }

    var key = url.hash.replace('#', '');
    var label = ANCHORS[key];
    if (!label) return;

    var samePage = url.origin === window.location.origin && url.pathname === window.location.pathname;
    if (!samePage) return; // let the normal navigation happen; handleInitialHash() picks it up after load

    var heading = findSectionHeading(label);
    if (!heading) return; // section not found for some reason — fall back to native navigation

    event.preventDefault();

    latestNavToken++;
    var myToken = latestNavToken;

    // The link may live inside the mobile drawer menu. Clicking it used to close the
    // drawer as a side effect of the page navigating away; since we no longer navigate
    // away, close it ourselves so it doesn't sit open over the section we just jumped to.
    // header-drawer's `isOpen` is a getter (a boolean), not a method — calling it as
    // one throws. `close` is the actual callable method.
    var headerDrawer = document.querySelector('header-drawer');
    var drawerWasOpen = !!(headerDrawer && headerDrawer.isOpen);
    if (drawerWasOpen && typeof headerDrawer.close === 'function') {
      headerDrawer.close();
    }

    // Start the scroll right away — scrollIntoView is a native, compositor-driven
    // animation that keeps running smoothly underneath the drawer's own closing
    // animation, so there's no need to wait for the drawer before kicking it off (unlike
    // the hand-rolled version this replaced, which had to serialize the two to avoid
    // visibly fighting each other).
    scrollToHeading(heading);

    // The URL update alone is cheap to delay/guard: only the most recent click should
    // win if the visitor clicks a second category before this fires.
    //
    // replaceState, not pushState: this only updates the address bar to reflect where the
    // visitor already is on the page — it is not a new "page" to remember. pushState would
    // stack up a new back/forward-history entry per click, and an accidental back gesture
    // (easy to trigger while scrolling — see the note at the top of this file) landing on
    // one of those stacked entries is what was forcing the full-page reloads seen in
    // testing. replaceState keeps the URL in sync without leaving anything behind for a
    // stray "back" to trip over.
    window.setTimeout(
      function () {
        if (myToken !== latestNavToken) return;
        history.replaceState(null, '', url.hash);
      },
      0
    );
  });
})();
